/**
 * One-screen best/worst mode for max_diff (spec 36-02).
 *
 * A thin shell over `bestWorst.ts`: every option renders once, tapping it cycles the option
 * through `cycleOption` (unselected -> best -> worst -> unselected, skipping a full side), and
 * Next is gated on `isComplete`. All ranking logic lives in `bestWorst.ts` and is unit-tested
 * there with no DOM; this component only renders that state and wires the tap handler.
 *
 * Carries its OWN Next button rather than the shared bottom one: the shared button's
 * `canProceed` check is keyed on the legacy `{most_appealing, least_appealing}` shape, which
 * this mode's `{best, worst}` value does not have. `SurveySession.tsx` hides the shared button
 * for this mode so the two affordances never both render.
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT } from '../../config/ui-text';
import {
  BestWorstState,
  Quotas,
  cycleOption,
  emptyState,
  isComplete,
  toResponseValue,
} from './bestWorst';

const SIDE_STYLES = {
  best: {
    selected: 'border-green-500 bg-green-50 text-green-900',
    badge: 'bg-green-500 text-white',
  },
  worst: {
    selected: 'border-red-500 bg-red-50 text-red-900',
    badge: 'bg-red-500 text-white',
  },
} as const;

const UNSELECTED_TILE_CLASS = 'border-gray-300 bg-white text-gray-900 hover:border-blue-300';

export interface BestWorstCardProps {
  options: string[];
  nBest: number;
  nWorst: number;
  onSubmit: (value: ReturnType<typeof toResponseValue>) => void;
}

function sideAndRank(state: BestWorstState, option: string): { side: 'best' | 'worst' | null; rank: number | null } {
  const bestIndex = state.best.indexOf(option);
  if (bestIndex >= 0) return { side: 'best', rank: bestIndex + 1 };
  const worstIndex = state.worst.indexOf(option);
  if (worstIndex >= 0) return { side: 'worst', rank: worstIndex + 1 };
  return { side: null, rank: null };
}

function counterText(state: BestWorstState, quotas: Quotas): string {
  return `${state.best.length} of ${quotas.nBest} best · ${state.worst.length} of ${quotas.nWorst} worst`;
}

/**
 * What to do next, in the respondent's words.
 *
 * The interaction is a cycle (tap = best, tap again = worst, again = clear) and nothing on screen
 * said so — found by taking the survey. Someone who had picked their bests and wanted to mark a
 * worst had no signpost, and someone tapping a second time to DESELECT would silently record the
 * opposite of what they meant. The hint follows the state so it always names the next useful move.
 */
const HINT_PICK_BEST = 'Tap the ones that matter MOST. Tap a chosen one again to move it to least.';
const HINT_PICK_WORST = 'Now tap the ones that matter LEAST.';
const HINT_DONE = 'Change any answer by tapping it again.';

function hintText(state: BestWorstState, quotas: Quotas): string {
  if (state.best.length < quotas.nBest) return HINT_PICK_BEST;
  if (state.worst.length < quotas.nWorst) return HINT_PICK_WORST;
  return HINT_DONE;
}

export const BestWorstCard: React.FC<BestWorstCardProps> = ({ options, nBest, nWorst, onSubmit }) => {
  const [state, setState] = useState<BestWorstState>(emptyState());
  const quotas: Quotas = { nBest, nWorst };
  const complete = isComplete(state, quotas);

  return (
    <div className="space-y-6">
      <div className="text-center mb-2">
        <div className="text-sm text-gray-500" role="status">{counterText(state, quotas)}</div>
        <div className="text-xs text-gray-600">
          {hintText(state, quotas)} • No right or wrong answers
        </div>
      </div>

      <div className="space-y-2">
        {options.map((option) => {
          const { side, rank } = sideAndRank(state, option);
          const styles = side ? SIDE_STYLES[side] : null;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setState((prev) => cycleOption(prev, option, quotas))}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between ${
                styles ? styles.selected : UNSELECTED_TILE_CLASS
              }`}
            >
              <span>{option}</span>
              {side && rank !== null && (
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${styles!.badge}`}>
                  {rank}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!complete}
          onClick={() => onSubmit(toResponseValue(state))}
          icon={<Check className="w-4 h-4" />}
        >
          {UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.NEXT}
        </Button>
      </div>
    </div>
  );
};

export default BestWorstCard;
